import { create } from 'zustand';
import { useDocumentStore } from './documentStore';
import { useUIStore } from './uiStore';

export type Tool =
  | 'select'
  | 'hand'
  | 'text-select'
  | 'highlight'
  | 'underline'
  | 'strikethrough'
  | 'text-annotation'
  | 'sticky-note'
  | 'freehand'
  | 'rectangle'
  | 'circle'
  | 'arrow'
  | 'line'
  | 'stamp'
  | 'signature'
  | 'measure-distance'
  | 'measure-area'
  | 'redact'
  | 'eraser';

export type HighlightColor = 'yellow' | 'green' | 'pink' | 'blue' | 'orange';

export interface AnnotationObject {
  id: string;
  pageNumber: number;
  type: Tool;
  data: Record<string, unknown>;
  color?: string;
  createdAt: number;
}

interface CanvasHistory {
  undoStack: string[];
  redoStack: string[];
  currentState: string;
}

interface AnnotationState {
  activeTool: Tool;
  highlightColor: HighlightColor;
  strokeColor: string;
  strokeWidth: number;
  fillColor: string;
  fontSize: number;
  fontFamily: string;
  opacity: number;
  annotations: Map<number, AnnotationObject[]>;
  selectedAnnotationId: string | null;
  fabricCanvases: Map<number, any>;
  canvasHistory: Map<number, CanvasHistory>;

  registerCanvas: (pageNumber: number, canvas: any) => void;
  unregisterCanvas: (pageNumber: number) => void;
  saveCanvasState: (pageNumber: number, jsonState: string) => void;
  exportAllPageOverlays: () => Promise<{ pageNumber: number; imageBytes: ArrayBuffer }[]>;

  setActiveTool: (tool: Tool) => void;
  setHighlightColor: (color: HighlightColor) => void;
  setStrokeColor: (color: string) => void;
  setStrokeWidth: (width: number) => void;
  setFillColor: (color: string) => void;
  setFontSize: (size: number) => void;
  setOpacity: (opacity: number) => void;
  addAnnotation: (pageNumber: number, annotation: AnnotationObject) => void;
  removeAnnotation: (pageNumber: number, annotationId: string) => void;
  clearAnnotations: (pageNumber: number) => void;
  selectAnnotation: (id: string | null) => void;

  undo: (targetPage?: number) => Promise<void>;
  redo: (targetPage?: number) => Promise<void>;
  canUndo: (targetPage?: number) => boolean;
  canRedo: (targetPage?: number) => boolean;
}

// Global flag to prevent history save loop during loadFromJSON
let isRestoringHistory = false;

export const useAnnotationStore = create<AnnotationState>((set, get) => ({
  activeTool: 'select',
  highlightColor: 'yellow',
  strokeColor: '#ef4444',
  strokeWidth: 2,
  fillColor: 'transparent',
  fontSize: 14,
  fontFamily: 'Inter',
  opacity: 1,
  annotations: new Map(),
  selectedAnnotationId: null,
  fabricCanvases: new Map(),
  canvasHistory: new Map(),

  registerCanvas: (pageNumber, canvas) => {
    const { fabricCanvases, canvasHistory } = get();
    const newCanvases = new Map(fabricCanvases);
    newCanvases.set(pageNumber, canvas);

    const newHistory = new Map(canvasHistory);
    const initialJson = JSON.stringify(canvas.toJSON());
    if (!newHistory.has(pageNumber)) {
      newHistory.set(pageNumber, {
        undoStack: [],
        redoStack: [],
        currentState: initialJson,
      });
    }
    set({ fabricCanvases: newCanvases, canvasHistory: newHistory });
  },

  unregisterCanvas: (pageNumber) => {
    const { fabricCanvases } = get();
    const newMap = new Map(fabricCanvases);
    newMap.delete(pageNumber);
    set({ fabricCanvases: newMap });
  },

  saveCanvasState: (pageNumber, jsonState) => {
    if (isRestoringHistory) return;

    const { canvasHistory } = get();
    const history = canvasHistory.get(pageNumber) || {
      undoStack: [],
      redoStack: [],
      currentState: '',
    };

    if (history.currentState === jsonState) return;

    const newUndo = [...history.undoStack, history.currentState].slice(-30);
    const newHistory = new Map(canvasHistory);
    newHistory.set(pageNumber, {
      undoStack: newUndo,
      redoStack: [], // Clear redo on new edit
      currentState: jsonState,
    });

    set({ canvasHistory: newHistory });
  },

  exportAllPageOverlays: async () => {
    const { fabricCanvases } = get();
    const overlays: { pageNumber: number; imageBytes: ArrayBuffer }[] = [];

    for (const [pageNumber, canvas] of fabricCanvases.entries()) {
      if (!canvas || canvas.getObjects().length === 0) continue;

      const dataUrl = canvas.toDataURL({ format: 'png', multiplier: 2 });
      const base64 = dataUrl.split(',')[1];
      const binaryStr = atob(base64);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }
      overlays.push({ pageNumber, imageBytes: bytes.buffer });
    }

    return overlays;
  },

  setActiveTool: (tool) => set({ activeTool: tool }),
  setHighlightColor: (color) => set({ highlightColor: color }),
  setStrokeColor: (color) => set({ strokeColor: color }),
  setStrokeWidth: (width) => set({ strokeWidth: width }),
  setFillColor: (color) => set({ fillColor: color }),
  setFontSize: (size) => set({ fontSize: size }),
  setOpacity: (opacity) => set({ opacity }),

  addAnnotation: (pageNumber, annotation) => {
    const { annotations } = get();
    const newAnnotations = new Map(annotations);
    const pageAnnotations = [...(newAnnotations.get(pageNumber) || []), annotation];
    newAnnotations.set(pageNumber, pageAnnotations);
    set({ annotations: newAnnotations });
  },

  removeAnnotation: (pageNumber, annotationId) => {
    const { annotations } = get();
    const newAnnotations = new Map(annotations);
    const pageAnnotations = (newAnnotations.get(pageNumber) || []).filter(
      (a) => a.id !== annotationId
    );
    newAnnotations.set(pageNumber, pageAnnotations);
    set({ annotations: newAnnotations });
  },

  clearAnnotations: (pageNumber) => {
    const { annotations } = get();
    const newAnnotations = new Map(annotations);
    newAnnotations.delete(pageNumber);
    set({ annotations: newAnnotations });
  },

  selectAnnotation: (id) => set({ selectedAnnotationId: id }),

  canUndo: (targetPage) => {
    const page = targetPage ?? useDocumentStore.getState().currentPage;
    const history = get().canvasHistory.get(page);
    return !!history && history.undoStack.length > 0;
  },

  canRedo: (targetPage) => {
    const page = targetPage ?? useDocumentStore.getState().currentPage;
    const history = get().canvasHistory.get(page);
    return !!history && history.redoStack.length > 0;
  },

  undo: async (targetPage) => {
    const page = targetPage ?? useDocumentStore.getState().currentPage;
    const { canvasHistory, fabricCanvases } = get();
    const history = canvasHistory.get(page);
    const canvas = fabricCanvases.get(page);

    if (!history || !canvas || history.undoStack.length === 0) {
      const didUndoDoc = await useDocumentStore.getState().undoDocChange();
      if (!didUndoDoc) {
        useUIStore.getState().addToast({ type: 'warning', message: 'Nothing to undo' });
      }
      return;
    }

    const newUndoStack = [...history.undoStack];
    const prevState = newUndoStack.pop()!;
    const newRedoStack = [...history.redoStack, history.currentState];

    const newHistory = new Map(canvasHistory);
    newHistory.set(page, {
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      currentState: prevState,
    });

    set({ canvasHistory: newHistory });

    isRestoringHistory = true;
    try {
      canvas.discardActiveObject();
      await canvas.loadFromJSON(JSON.parse(prevState));
      canvas.renderAll();
      useUIStore.getState().addToast({ type: 'info', message: '↩️ Undo' });
    } catch (e) {
      console.error('Undo failed:', e);
    } finally {
      isRestoringHistory = false;
    }
  },

  redo: async (targetPage) => {
    const page = targetPage ?? useDocumentStore.getState().currentPage;
    const { canvasHistory, fabricCanvases } = get();
    const history = canvasHistory.get(page);
    const canvas = fabricCanvases.get(page);

    if (!history || !canvas || history.redoStack.length === 0) {
      const didRedoDoc = await useDocumentStore.getState().redoDocChange();
      if (!didRedoDoc) {
        useUIStore.getState().addToast({ type: 'warning', message: 'Nothing to redo' });
      }
      return;
    }

    const newRedoStack = [...history.redoStack];
    const nextState = newRedoStack.pop()!;
    const newUndoStack = [...history.undoStack, history.currentState];

    const newHistory = new Map(canvasHistory);
    newHistory.set(page, {
      undoStack: newUndoStack,
      redoStack: newRedoStack,
      currentState: nextState,
    });

    set({ canvasHistory: newHistory });

    isRestoringHistory = true;
    try {
      canvas.discardActiveObject();
      await canvas.loadFromJSON(JSON.parse(nextState));
      canvas.renderAll();
      useUIStore.getState().addToast({ type: 'info', message: '↪️ Redo' });
    } catch (e) {
      console.error('Redo failed:', e);
    } finally {
      isRestoringHistory = false;
    }
  },
}));
